// Financial Reporting tab — per-item review, rendered INLINE under its row.
//
// This was a second drawer opened on top of the first. Reviewing one item then
// the next meant open drawer, act, close drawer, find the row again, open the
// next drawer — and the request the items belong to was behind whichever drawer
// was on top. It is now the expanded body of the row it belongs to, inside the
// one lifecycle drawer, so the list stays visible while a reviewer works down
// it.
//
// 🔴 WHY THIS IS NOT <StagedSubmissionReviewPanel>, WHOSE JOB THIS LOOKS LIKE.
//
// Its ACTIONS would bypass this module. It owns Approve / File / Return and
// calls `collectionStagingApi` directly at eight sites, while
// ReportRequestReviewService deliberately calls the staging service and is
// never called back by it. A B3 who approved inside that panel would file the
// document and approve the staged submission while `LoanReportRequestItem`
// stayed unapproved and the request's status never rolled forward — silent,
// green, and wrong. `hideQueueActions` does not hide those buttons; it hides
// Manage Assignees / Change owner.
//
// So its PRESENTATION is reused and its actions are not: the file renders
// through the shared `DocumentPreviewPane`, fed by the staging module's own
// read-only `download-url` endpoint, and every write goes through the
// report-request hooks, which do both halves in the right order.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button, StatusBadge, Textarea, useToast } from "@/design-system/components/ui";
import { DocumentPreviewPane } from "@/components/review/DocumentPreview";
import { FolderPicker } from "@/components/dms/FolderPicker";
import { collectionStagingApi } from "@/api/collectionStaging";
import { extractErrorMessage } from "@/api/client";
import { formatDateInTz } from "@/design-system/lib/format";
import {
  REPORT_REQUEST_ITEM_KIND_LABEL,
  REPORT_REQUEST_ITEM_STATUS_LABEL,
  type ReportRequestDto,
  type ReportRequestItemDto,
} from "@/api/portfolioAdmin/reportRequestsApi";
import {
  useApproveReportRequestItem,
  useReturnReportRequestItem,
} from "@/hooks/portfolioAdmin/useReportRequests";
import { hasSubmission } from "./reportRequestSummary";
import { reportRequestPreviewKeys } from "./reportRequestPreviewKeys";

interface ReportRequestItemReviewPanelProps {
  loanId: string;
  item: ReportRequestItemDto;
  /** The request the item belongs to — for the package link + period context. */
  request: ReportRequestDto;
  /** Reporting.ReviewRequests. A reader sees the facts and the file, no actions. */
  canReview: boolean;
  tz?: string | null;
}

/**
 * The borrower's file, from the staged submission the review actions act on.
 *
 * Read-only, and gated by the staging controller's own
 * `Collection.Staging.ReviewAndFile` — which every non-ADMIN role holds through
 * the open-posture bundle, so a B3 who can reach this panel can reach the file.
 */
function useSubmittedFile(stagedSubmissionId: string | null) {
  return useQuery({
    queryKey: reportRequestPreviewKeys.submission(stagedSubmissionId ?? ""),
    queryFn: () => collectionStagingApi.downloadUrl(stagedSubmissionId!),
    enabled: !!stagedSubmissionId,
    // A SAS URL has an expiry on it; re-fetching per open is cheaper than
    // handing the reader a dead iframe.
    staleTime: 60_000,
    retry: false,
  });
}

export function ReportRequestItemReviewPanel({
  loanId,
  item,
  request,
  canReview,
  tz,
}: ReportRequestItemReviewPanelProps) {
  const toast = useToast();
  const approve = useApproveReportRequestItem(loanId);
  const returnItem = useReturnReportRequestItem(loanId);

  const [folderId, setFolderId] = useState<string | null>(null);
  const [reviewerNote, setReviewerNote] = useState("");
  const [returnMode, setReturnMode] = useState(false);
  const [returnNote, setReturnNote] = useState("");

  // Reset per item — a note typed against one item must never ride along to
  // the next one opened.
  useEffect(() => {
    setFolderId(null);
    setReviewerNote("");
    setReturnMode(false);
    setReturnNote("");
  }, [item.id]);

  const isApproved = item.status === "approved";
  // 🔴 Both actions run through StagedSubmissionService, which refuses File /
  // Approve / Return on any staged row that is not AwaitingReview. So that —
  // not a timestamp, and not `status` — is the condition mirrored here.
  //
  // This used to read `item.submittedAt === null`, which was ALWAYS true: the
  // field was projected from a column no writer ever set, so the panel withheld
  // approve and return from every item, on every request, for its whole life.
  // Nothing 400d and nothing logged, because a surface that never offers a
  // button never makes a call that can fail.
  const awaitingReview = item.awaitingReview;
  // Told apart because they need different sentences: one borrower has sent
  // nothing, the other has sent something that has already been dealt with.
  const everSubmitted = hasSubmission(item);
  const pending = approve.isPending || returnItem.isPending;

  const file = useSubmittedFile(everSubmitted ? item.stagedSubmissionId : null);

  const submitApprove = () => {
    if (!folderId) return;
    approve.mutate(
      { itemId: item.id, body: { folderId, reviewerNote: reviewerNote.trim() || null } },
      {
        onSuccess: () => toast.success("Item approved and filed."),
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  const submitReturn = () => {
    const note = returnNote.trim();
    if (!note) return;
    returnItem.mutate(
      { itemId: item.id, note },
      {
        onSuccess: () => toast.success("Item returned to the borrower."),
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <div
      className="flex flex-col gap-space-4"
      data-testid={`report-request-item-panel-${item.id}`}
    >
      <div className="flex flex-wrap items-center gap-space-2">
        <StatusBadge tone={isApproved ? "ok" : awaitingReview ? "info" : "neutral"}>
          {REPORT_REQUEST_ITEM_STATUS_LABEL[item.status] ?? item.status}
        </StatusBadge>
        <StatusBadge tone="neutral">
          {REPORT_REQUEST_ITEM_KIND_LABEL[item.kind] ?? item.kind}
        </StatusBadge>
        {item.guarantorLabel && <StatusBadge tone="info">{item.guarantorLabel}</StatusBadge>}
        {/* 🔴 "Submitted" is stated from the staged submission, never from
            `status`: nothing writes ReportRequestItemStatuses.Submitted, so a
            badge derived from it would read "Not yet provided" over a document
            sitting right there in the pane below. */}
        <span className="text-caption text-gray-mid">
          {item.submittedAt
            ? `Submitted ${formatDateInTz(item.submittedAt, tz)}`
            : "Nothing submitted yet"}
        </span>
        {item.approvedAt && (
          <span className="text-caption text-gray-mid">
            · Approved {formatDateInTz(item.approvedAt, tz)}
            {item.approvedByUserName ? ` by ${item.approvedByUserName}` : ""}
          </span>
        )}
      </div>

      {item.reviewerNote && (
        <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
          <p className="text-caption text-gray-mid m-0">Your last note</p>
          <p className="text-small text-onyx m-0 mt-space-1">{item.reviewerNote}</p>
        </div>
      )}

      {/* The file, in the panel. Approving something the reviewer never saw is
          the failure this closes — it used to be a link out to the borrower's
          package, on the grounds that the blob/SAS plumbing lived in the shared
          staging drawer. It lives in a read-only endpoint that takes exactly
          the id the item DTO now carries. */}
      {everSubmitted && (
        <div data-testid={`report-request-item-preview-${item.id}`}>
          <DocumentPreviewPane
            document={{
              title: item.displayName,
              fileName: null,
              url: file.data?.previewUrl ?? file.data?.url ?? null,
              isPdf: file.data?.isPdf ?? null,
              state: file.isLoading
                ? "loading"
                : file.isError
                  ? "error"
                  : file.data
                    ? "ready"
                    : "unavailable",
              message: file.isError
                ? "Could not load the submitted file. Open the borrower's request to read it."
                : null,
              onRetry: () => void file.refetch(),
            }}
          />
        </div>
      )}

      {request.collectionPackageId && (
        <p className="text-caption text-gray-mid m-0">
          <Link
            to={`/collection/packages/${request.collectionPackageId}`}
            className="inline-flex items-center gap-space-1 text-gold-shadow hover:underline underline-offset-4"
          >
            Open the borrower&apos;s request
            <ArrowRight size={12} aria-hidden />
          </Link>{" "}
          for everything they sent on this bundle.
        </p>
      )}

      {/* isApproved is tested FIRST. An approved item's staged submission is no
          longer awaiting review, so it satisfies that condition too — and under
          the other ordering it read "the borrower has not submitted anything"
          about a document Phoenix had already accepted and filed. */}
      {isApproved ? (
        <p
          className="text-small text-status-success m-0"
          data-testid={`item-already-approved-${item.id}`}
        >
          Approved and filed. Returning it would reopen it for the borrower —
          issue a fresh request instead.
        </p>
      ) : !everSubmitted ? (
        <p
          className="text-small text-gray-mid m-0"
          data-testid={`item-nothing-submitted-${item.id}`}
        >
          The borrower has not submitted anything for this item yet, so there is
          nothing to approve or return.
        </p>
      ) : !awaitingReview ? (
        <p
          className="text-small text-gray-mid m-0"
          data-testid={`item-nothing-awaiting-review-${item.id}`}
        >
          The borrower&apos;s last submission for this item has already been
          reviewed, so there is nothing waiting on you. It reappears here once
          they submit again.
        </p>
      ) : !canReview ? (
        <p className="text-small text-gray-mid m-0" data-testid={`item-read-only-${item.id}`}>
          Waiting on a reviewer. Approving and returning need the report-request
          reviewer permission.
        </p>
      ) : !returnMode ? (
        <>
          <section className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2">
            <h4 className="text-small font-semibold text-onyx m-0">File into folder</h4>
            <FolderPicker
              value={folderId}
              onChange={(id) => setFolderId(id)}
              treeSource={{ kind: "entity", entityType: "Loan", entityId: loanId }}
              search={{ placeholder: "Search every folder by name…" }}
              browseByDefault
            />
          </section>

          <div>
            <label
              className="block text-small font-medium text-onyx mb-space-1"
              htmlFor={`item-reviewer-note-${item.id}`}
            >
              Note <span className="text-gray-mid font-normal">(optional)</span>
            </label>
            <Textarea
              id={`item-reviewer-note-${item.id}`}
              value={reviewerNote}
              onChange={(e) => setReviewerNote(e.target.value)}
              rows={2}
              placeholder="Kept on the item, and available to the generated report…"
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-space-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReturnMode(true)}
              disabled={pending}
              data-testid={`item-return-open-${item.id}`}
            >
              Return with comments
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!folderId || pending}
              loading={approve.isPending}
              onClick={submitApprove}
              data-testid={`item-approve-${item.id}`}
            >
              Approve &amp; file
            </Button>
          </div>
          {!folderId && (
            <p className="text-caption text-gray-mid m-0 text-right">
              Choose a folder to file it into.
            </p>
          )}
        </>
      ) : (
        <>
          <div>
            <label
              className="block text-small font-medium text-onyx mb-space-1"
              htmlFor={`item-return-note-${item.id}`}
            >
              What needs fixing <span className="text-status-error">*</span>
            </label>
            <Textarea
              id={`item-return-note-${item.id}`}
              value={returnNote}
              onChange={(e) => setReturnNote(e.target.value)}
              rows={4}
              placeholder="Tell the borrower exactly what to change…"
              data-testid={`item-return-note-${item.id}`}
            />
            <p className="text-caption text-gray-mid m-0 mt-space-1">
              Only this item reopens in the borrower&apos;s checklist. Anything you
              have already approved on this request stays approved.
            </p>
          </div>
          <div className="flex items-center justify-end gap-space-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReturnMode(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={returnNote.trim().length === 0 || pending}
              loading={returnItem.isPending}
              onClick={submitReturn}
              data-testid={`item-return-confirm-${item.id}`}
            >
              Return to borrower
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
